/**
 * Theme. Dark is the product's default; light is not an afterthought.
 *
 * `index.html` stamps `data-theme` on `<html>` before first paint from the same
 * `af.theme` key, so there is no flash. That inline script reads the value as a
 * **raw string**, which is why this file deliberately does not use
 * `useLocalStorage` (which JSON-encodes).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type ThemeMode = 'dark' | 'light' | 'system';
export type ResolvedTheme = 'dark' | 'light';

export const THEME_STORAGE_KEY = 'af.theme';
const LIGHT_QUERY = '(prefers-color-scheme: light)';

export interface ThemeContextValue {
  /** What the user chose. */
  mode: ThemeMode;
  /** What is actually painted right now. */
  theme: ResolvedTheme;
  setMode: (mode: ThemeMode) => void;
  /** Flip between light and dark, leaving `system` behind. */
  toggle: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function isThemeMode(value: string | null): value is ThemeMode {
  return value === 'dark' || value === 'light' || value === 'system';
}

function readStoredMode(): ThemeMode {
  if (typeof window === 'undefined') return 'dark';
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isThemeMode(stored) ? stored : 'system';
  } catch {
    return 'system';
  }
}

function systemTheme(): ResolvedTheme {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'dark';
  return window.matchMedia(LIGHT_QUERY).matches ? 'light' : 'dark';
}

export function resolveTheme(mode: ThemeMode): ResolvedTheme {
  return mode === 'system' ? systemTheme() : mode;
}

export interface ThemeProviderProps {
  children: ReactNode;
  /** Escape hatch for screenshot harnesses. */
  defaultMode?: ThemeMode;
}

export function ThemeProvider({ children, defaultMode }: ThemeProviderProps) {
  const [mode, setModeState] = useState<ThemeMode>(() => defaultMode ?? readStoredMode());
  const [theme, setTheme] = useState<ResolvedTheme>(() => resolveTheme(defaultMode ?? readStoredMode()));

  // Paint the attribute the tokens hang off.
  useEffect(() => {
    const resolved = resolveTheme(mode);
    setTheme(resolved);
    document.documentElement.dataset.theme = resolved;
  }, [mode]);

  // Follow the OS while in system mode.
  useEffect(() => {
    if (mode !== 'system' || typeof window.matchMedia !== 'function') return;
    const media = window.matchMedia(LIGHT_QUERY);
    const onChange = (event: MediaQueryListEvent) => {
      const resolved: ResolvedTheme = event.matches ? 'light' : 'dark';
      setTheme(resolved);
      document.documentElement.dataset.theme = resolved;
    };
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [mode]);

  // A second tab switching theme should not leave this one out of step.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== THEME_STORAGE_KEY) return;
      if (isThemeMode(event.newValue)) setModeState(event.newValue);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      /* Private mode / storage disabled — the theme still applies for this session. */
    }
  }, []);

  const toggle = useCallback(() => {
    setModeState((current) => {
      const next: ThemeMode = resolveTheme(current) === 'dark' ? 'light' : 'dark';
      try {
        window.localStorage.setItem(THEME_STORAGE_KEY, next);
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ mode, theme, setMode, toggle }),
    [mode, theme, setMode, toggle],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used inside <ThemeProvider>.');
  return context;
}

export const THEME_OPTIONS: Array<{ value: ThemeMode; label: string }> = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'system', label: 'System' },
];
